<script context="module">
  /**
   * Valid layouts for the CardLayout component.
   * @typedef {Object} VALID_LAYOUTS
   * @property {string} TwoColumns - Two columns layout.
   * @property {string} ThreeColumns - Three columns layout.
   * @property {string} FourColumns - Four columns layout.
   * @property {string} SixColumns - Six columns layout.
   */

  export const VALID_LAYOUTS = /** @type {const} **/ ({
    TwoColumns: "twoColumns",
    ThreeColumns: "threeColumns",
    FourColumns: "fourColumns",
    SixColumns: "sixColumns"
  });

  export const DEFAULT_LAYOUT = /** @type { const } */ "default";
</script>

<script>
  // @ts-check
  import { setContext } from "svelte";

  import GridContainer from "../atoms/GridContainer.svelte";
  import GridDivider from "../atoms/GridDivider.svelte";
  import Grid from "../atoms/Grid.svelte";
  import { validatePropValue } from "../utilities/logs";
  import {
    SPLIT_LAYOUT_VARIANTS,
    SPLIT_LAYOUT_RIGHT_GRID_COLUMNS
  } from "../organisms/SplitLayout.svelte";

  export let id = undefined;

  /**
   * @component CardLayout
   * @description A layout component that displays items in a grid layout with customizable number of columns.
   *
   * @prop {string} id - The ID of the component. Used for testing
   * @prop {string} containerClass - Class to add to container
   * @prop {VALID_LAYOUTS[keyof VALID_LAYOUTS]} layout - The layout of the grid. Must be one of the values defined in VALID_LAYOUTS.
   * @prop {*} component - The Svelte component to render for each item.
   * @prop {Array} items - The array of items to render.
   * @prop {number} mobileColumnCount - The number of columns to display on mobile.
   */

  export let layout = VALID_LAYOUTS.TwoColumns;

  validatePropValue({
    componentName: "CardLayout",
    propName: "layout",
    propValue: layout,
    validPropValues: Object.values(VALID_LAYOUTS)
  });
  export let component;
  export let items = [];
  export let containerClass = "";
  export let mobileColumnCount = 1;

  /**
   * The split layout variant
   * @type  { SPLIT_LAYOUT_VARIANTS[keyof SPLIT_LAYOUT_VARIANTS] }
   */
  export let splitLayoutVariant = null;

  const gridClass = "gap-y-15 xl:gap-y-20";
  const borderClasses =
    mobileColumnCount === 1
      ? "before:absolute before:-top-7.5 before:left-0 before:w-full before:h-px before:bg-current [&:first-child]:before:hidden md:before:hidden"
      : "";
  const mobileColumnClass = mobileColumnCount === 1 ? "col-span-4" : "col-span-2";
  const gridItemClasses = {
    [VALID_LAYOUTS.TwoColumns]: {
      [SPLIT_LAYOUT_VARIANTS.LAYOUT_50_50]: "md:col-span-6 lg:col-span-3 ",
      [SPLIT_LAYOUT_VARIANTS.LAYOUT_25_75]: "md:col-span-3 lg:col-span-4",
      [SPLIT_LAYOUT_VARIANTS.LAYOUT_75_25]: "md:col-span-2 lg:col-span-1",
      [DEFAULT_LAYOUT]: "md:col-span-6"
    },
    [VALID_LAYOUTS.ThreeColumns]: {
      [SPLIT_LAYOUT_VARIANTS.LAYOUT_50_50]: "md:col-span-4 lg:col-span-2",
      [SPLIT_LAYOUT_VARIANTS.LAYOUT_25_75]: "md:col-span-2 lg:col-span-3",
      [SPLIT_LAYOUT_VARIANTS.LAYOUT_75_25]: "md:col-span-1 lg:col-span-1",
      [DEFAULT_LAYOUT]: "md:col-span-6 lg:col-span-4"
    },
    [VALID_LAYOUTS.FourColumns]: {
      [SPLIT_LAYOUT_VARIANTS.LAYOUT_50_50]: "md:col-span-3 lg:col-span-1",
      [SPLIT_LAYOUT_VARIANTS.LAYOUT_25_75]: "md:col-span-1 lg:col-span-2",
      [SPLIT_LAYOUT_VARIANTS.LAYOUT_75_25]: "md:col-span-1 lg:col-span-1",
      [DEFAULT_LAYOUT]: "md:col-span-6 lg:col-span-3"
    },
    [VALID_LAYOUTS.SixColumns]: {
      [SPLIT_LAYOUT_VARIANTS.LAYOUT_50_50]: "md:col-span-2 lg:col-span-1",
      [SPLIT_LAYOUT_VARIANTS.LAYOUT_25_75]: "md:col-span-1 lg:col-span-1",
      [SPLIT_LAYOUT_VARIANTS.LAYOUT_75_25]: "md:col-span-1 lg:col-span-1",
      [DEFAULT_LAYOUT]: "md:col-span-3 lg:col-span-2"
    }
  };

  const dividerTable = /** @type {const} */ ({
    [VALID_LAYOUTS.TwoColumns]: { mobile: 2, tablet: 2, desktop: 2 },
    [VALID_LAYOUTS.ThreeColumns]: {
      mobile: 2,
      tablet: splitLayoutVariant ? 3 : 2,
      desktop: 3
    },
    [VALID_LAYOUTS.FourColumns]: {
      mobile: 2,
      tablet: splitLayoutVariant ? 4 : 2,
      desktop: 4
    },
    [VALID_LAYOUTS.SixColumns]: { mobile: mobileColumnCount === 1 ? 4 : 2, tablet: 4, desktop: 6 }
  });
  const firstDividerContainerClasses = `${mobileColumnCount === 1 ? "hidden" : "block"} md:hidden`;

  const isValidLayout = Object.values(VALID_LAYOUTS).includes(layout);
  const gridItemClass = gridItemClasses[layout]
    ? gridItemClasses[layout][splitLayoutVariant || DEFAULT_LAYOUT]
    : null;

  setContext("CardLayout", { id, layout, component });
</script>

{#if items.length > 0 && !!component && isValidLayout}
  <GridContainer
    {id}
    variant={splitLayoutVariant ? "bleed" : "regular"}
    {containerClass}
    gridInnerClasses="relative"
  >
    <!-- Card Dividers -->
    <GridDivider
      subGridColumns={SPLIT_LAYOUT_RIGHT_GRID_COLUMNS[splitLayoutVariant]}
      numberOfColumns={dividerTable[layout].mobile}
      containerClasses={firstDividerContainerClasses}
    />
    <GridDivider
      subGridColumns={SPLIT_LAYOUT_RIGHT_GRID_COLUMNS[splitLayoutVariant]}
      numberOfColumns={dividerTable[layout].tablet}
      containerClasses="hidden md:block lg:hidden"
    />
    <GridDivider
      subGridColumns={SPLIT_LAYOUT_RIGHT_GRID_COLUMNS[splitLayoutVariant]}
      numberOfColumns={dividerTable[layout].desktop}
      containerClasses="hidden lg:block"
    />
    <!-- Card Dividers -->
    <Grid {gridClass} subGridColumns={SPLIT_LAYOUT_RIGHT_GRID_COLUMNS[splitLayoutVariant]}>
      {#each items as item}
        <div
          class="${mobileColumnClass} relative {gridItemClass} {borderClasses} {mobileColumnClass}"
        >
          <svelte:component this={component} {...item} />
        </div>
      {/each}
    </Grid>
  </GridContainer>
{/if}
